import { BufferAttribute, BufferGeometry } from 'three';

import { isBufferAttribute } from '../utils/index.js';
const NAME = 'ProjectionGeneratorWorker';

export class ProjectionGeneratorWorker {
  running: boolean;
  worker: Worker | null;
  constructor(url: string) {
    this.running = false;
    this.worker = new Worker(url, {
      type: 'module',
    });
    this.worker.onerror = (e) => {
      if (e.message) {
        throw new Error(`${NAME}: Could not create Web Worker with error "${e.message}"`);
      } else {
        throw new Error(`${NAME}: Could not create Web Worker.`);
      }
    };
  }

  generate(
    geometry: BufferGeometry,
    options?: {
      sortEdges?: boolean;
      angleThreshold?: number;
      onProgress?: (progress: number) => void;
    }
  ) {
    const { onProgress } = { ...options };
    if (this.running) {
      throw new Error(`${NAME}: Already running job.`);
    }

    if (this.worker === null) {
      throw new Error(`${NAME}: Worker has been disposed.`);
    }

    const { worker } = this;
    this.running = true;

    return new Promise<BufferGeometry>((resolve, reject) => {
      worker.onerror = (e) => {
        reject(new Error(`${NAME}: ${e.message}`));
        this.running = false;
      };

      worker.onmessage = (e) => {
        this.running = false;
        const { data } = e;

        if (data.error) {
          reject(new Error(data.error));
          worker.onmessage = null;
        } else if (data.result) {
          const geometry = new BufferGeometry();
          geometry.setAttribute('position', new BufferAttribute(data.result, 3, false));
          resolve(geometry);
          worker.onmessage = null;
        } else if (onProgress) {
          onProgress(data.progress);
        }
      };

      if (!isBufferAttribute(geometry.attributes.position)) {
        throw new Error('ProjectionGeneratorWorker: Position attribute is not a BufferAttribute.');
      }

      const index = geometry.index ? (geometry.index.array as Float32Array).slice() : null;
      const position = (geometry.attributes.position.array as Float32Array).slice();
      const transfer = [position.buffer];
      if (index) {
        transfer.push(index.buffer);
      }

      worker.postMessage(
        {
          index,
          position,
          options: {
            ...options,
            onProgress: null,
            includedProgressCallback: Boolean(onProgress),
          },
        },
        transfer
      );
    });
  }

  dispose() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}
